import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Link as MuiLink,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Business,
  Email,
  Phone,
  LocationOn,
  Info,
  DesignServices,
  Groups,
  People,
  Work,
  ContactMail,
  Code,
  Close,
  PhotoLibrary,
  School,
  Quiz,
  Cloud,
  Security,
  Build,
  Dashboard,
  FileCopy,
  AttachMoney,
  Public,
  Extension,
  Route,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import logo from '../images/logo.png';
import PolicyLinks from './PolicyLinks';

// ---------- Galaxy Background Animations ----------
const twinkle = keyframes`
  0% { opacity: 0.2; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0.2; transform: scale(0.8); }
`;

const glowAnimation = keyframes`
  0% { text-shadow: 0 0 10px rgba(0, 240, 255, 0.3), 0 0 20px rgba(0, 240, 255, 0.1); }
  50% { text-shadow: 0 0 20px rgba(0, 240, 255, 0.6), 0 0 40px rgba(0, 240, 255, 0.3), 0 0 60px rgba(0, 240, 255, 0.1); }
  100% { text-shadow: 0 0 10px rgba(0, 240, 255, 0.3), 0 0 20px rgba(0, 240, 255, 0.1); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-8px) rotate(2deg); }
  66% { transform: translateY(8px) rotate(-2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const reduceMotionSx = {
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none !important',
    transition: 'none !important',
  },
};

const Footer = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const handleDialogOpen = () => setOpenDialog(true);
  const handleDialogClose = () => setOpenDialog(false);

  const socialIcons = [
    {
      icon: <Facebook fontSize="medium" />,
      color: '#4267B2',
      name: 'Facebook',
      url: 'https://www.facebook.com/people/AediaX-Tech-Private-Limited/61576841507856/',
    },
    {
      icon: <Twitter fontSize="medium" />,
      color: '#1DA1F2',
      name: 'Twitter',
      url: 'https://twitter.com/aediax_tech',
    },
    {
      icon: <Instagram fontSize="medium" />,
      color: '#E1306C',
      name: 'Instagram',
      url: 'https://www.instagram.com/aediax_tech/',
    },
    {
      icon: <LinkedIn fontSize="medium" />,
      color: '#0077B5',
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/company/aediax-tech-private-limited',
    },
  ];

  const mainLinks = [
    { icon: <Info fontSize="small" />, text: 'About Us', href: '/about' },
    { icon: <DesignServices fontSize="small" />, text: 'Services', href: '/services' },
    { icon: <Groups fontSize="small" />, text: 'Our Team', href: '/team' },
    { icon: <Work fontSize="small" />, text: 'Workflow', href: '/workflow' },
    { icon: <People fontSize="small" />, text: 'Clients', href: '/clients' },
    { icon: <PhotoLibrary fontSize="small" />, text: 'Gallery', href: '/gallery' },
    { icon: <ContactMail fontSize="small" />, text: 'Contact', href: '/contact' },
    { icon: <Public fontSize="small" />, text: 'Projects', href: '/projects' },
    { icon: <Route fontSize="small" />, text: 'Interns', href: '/intern' },
    { icon: <FileCopy fontSize="small" />, text: 'Logo', href: '/logo' },
  ];

  const servicesLinks = [
    { icon: <DesignServices fontSize="small" />, text: 'Consultancy', href: '/services/consultancy' },
    { icon: <Business fontSize="small" />, text: 'App Development', href: '/services/app-development' },
    { icon: <Build fontSize="small" />, text: 'Automation', href: '/services/automation' },
    { icon: <Cloud fontSize="small" />, text: 'SaaS', href: '/services/saas' },
    { icon: <Dashboard fontSize="small" />, text: 'MVP', href: '/services/mvp' },
    { icon: <Extension fontSize="small" />, text: 'More Services', href: '/services/more' },
  ];

  const internshipLinks = [
    { icon: <School fontSize="small" />, text: 'Internships', href: '/services/internships' },
    { icon: <FileCopy fontSize="small" />, text: 'Certificate Portal', href: '/internship/certificate-portal' },
    { icon: <Work fontSize="small" />, text: 'Internship Form', href: '/services/internships/form' },
    { icon: <AttachMoney fontSize="small" />, text: 'Payment', href: '/services/internships/form/payment' },
  ];

  const subdomainLinks = [
    { icon: <School fontSize="small" />, text: 'Education', href: 'https://education.aediax.com', external: true },
    { icon: <Quiz fontSize="small" />, text: 'Exam', href: 'https://exam.aediax.com', external: true },
    { icon: <Cloud fontSize="small" />, text: 'Edge', href: 'https://edge.aediax.com', external: true },
    { icon: <Security fontSize="small" />, text: 'Steganography', href: 'https://steganography.aediax.com', external: true },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.15 },
    },
  };

  const columnVariants = (fromX = 0) => ({
    hidden: { y: 30, x: fromX, opacity: 0 },
    visible: {
      y: 0,
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
  });

  const SectionHeading = ({ children, small }) => (
    <Typography
      variant="subtitle2"
      gutterBottom
      sx={{
        fontWeight: 700,
        fontSize: small ? '0.75rem' : '0.8rem',
        color: '#ffffff',
        display: 'inline-block',
        position: 'relative',
        mt: small ? 1.5 : 0,
        pb: 0.3,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: '2px',
          width: '30%',
          background: 'linear-gradient(90deg, #00f0ff, rgba(0, 240, 255, 0.1))',
          transition: 'width 0.4s ease',
          ...reduceMotionSx,
        },
        '&:hover::after': { width: '100%' },
      }}
    >
      {children}
    </Typography>
  );

  const renderLink = (link, index) => (
    <MuiLink
      key={index}
      href={link.href}
      target={link.external ? '_blank' : '_self'}
      rel={link.external ? 'noopener noreferrer' : ''}
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 0.8,
        color: 'rgba(200, 200, 200, 0.6)',
        textDecoration: 'none',
        fontSize: '0.75rem',
        transition: 'all 0.3s ease',
        position: 'relative',
        '&:hover': {
          color: '#00f0ff',
          transform: 'translateX(4px)',
        },
      }}
      component={motion.a}
      whileHover={{ x: 4 }}
    >
      <Box
        component="span"
        sx={{
          mr: 1,
          display: 'flex',
          alignItems: 'center',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.2) rotate(-5deg)',
            color: '#FFD700',
          },
        }}
      >
        {link.icon}
      </Box>
      {link.text}
    </MuiLink>
  );

  // Generate random stars for background - REDUCED from 80 to 25
  const generateStars = (count) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 1.5 + 0.5,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2,
      opacity: Math.random() * 0.4 + 0.2,
    }));
  };

  const stars = generateStars(25);

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#0a0a12',
        color: '#e0e0e0',
        py: { xs: 3, sm: 4, md: 5 },
        borderTop: '1px solid rgba(0, 240, 255, 0.08)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(ellipse at 20% 50%, rgba(0, 0, 0, 0.4) 0%, transparent 60%),
            radial-gradient(ellipse at 80% 20%, rgba(0, 0, 0, 0.3) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 80%, rgb(0, 0, 0) 0%, transparent 50%),
            radial-gradient(ellipse at 0% 80%, rgb(0, 0, 0) 0%, transparent 40%),
            radial-gradient(ellipse at 100% 60%, rgb(0, 0, 0) 0%, transparent 40%)
          `,
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      {/* Galaxy Background Stars - Reduced quantity */}
      {stars.map((star) => (
        <Box
          key={star.id}
          sx={{
            position: 'absolute',
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            backgroundColor: '#ffffff',
            borderRadius: '50%',
            animation: `${twinkle} ${star.duration}s ease-in-out ${star.delay}s infinite`,
            opacity: star.opacity,
            zIndex: 0,
            pointerEvents: 'none',
            ...reduceMotionSx,
          }}
        />
      ))}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Column 1: Company Info */}
          <Box 
            component={motion.div} 
            variants={columnVariants(-20)}
            sx={{
              gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'auto' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1.5,
                position: 'relative',
              }}
            >
              <Box
                component={motion.img}
                src={logo}
                alt="AediaX Logo"
                whileHover={{
                  rotate: [-5, 5, 0],
                  scale: 1.1,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 15,
                  rotate: { duration: 0.5 },
                }}
                sx={{
                  height: { xs: 35, sm: 40, md: 45 },
                  width: 'auto',
                  mr: 1.5,
                  filter: 'drop-shadow(0 0 20px rgba(0, 240, 255, 0.3))',
                  transition: 'filter 0.3s ease',
                  '&:hover': {
                    filter: 'drop-shadow(0 0 30px rgba(0, 240, 255, 0.6))',
                  },
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    mb: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.3,
                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: '#00f0ff',
                      animation: `${glowAnimation} 3s ease-in-out infinite`,
                      ...reduceMotionSx,
                    }}
                  >
                    Aedia
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: '#FFD700',
                      display: 'inline-block',
                      animation: `${glowAnimation} 3s ease-in-out infinite 0.5s`,
                      ...reduceMotionSx,
                    }}
                  >
                    X
                  </Box>
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: 'rgb(255, 255, 255)',
                mb: 1.5,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                lineHeight: 1.5,
              }}
            >
              "Empowering businesses with intelligent solutions for the digital age"
            </Typography>

            <Box
              component={motion.div}
              whileHover={{
                borderColor: 'rgba(0, 240, 255, 0.3)',
                backgroundColor: 'rgba(0, 240, 255, 0.08)',
              }}
              sx={{
                mb: 1.5,
                p: 1,
                backgroundColor: 'rgba(0, 240, 255, 0.03)',
                borderRadius: 1,
                border: '1px solid rgba(0, 240, 255, 0.06)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent, rgba(0, 240, 255, 0.03), transparent)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s ease',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', fontSize: '0.7rem' }}>
                Corporate Identification Number
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#00f0ff',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  letterSpacing: '0.5px',
                  fontSize: '0.9rem',
                }}
              >
                U62011OD2025PTC049519
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
              {socialIcons.map((social, index) => (
                <MuiLink
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: hoveredIcon === index ? social.color : 'rgba(255, 255, 255, 0.86)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -4,
                      left: '50%',
                      width: 0,
                      height: '2px',
                      background: social.color,
                      transition: 'all 0.3s ease',
                      transform: 'translateX(-50%)',
                    },
                    '&:hover::after': {
                      width: '100%',
                    },
                  }}
                  onMouseEnter={() => setHoveredIcon(index)}
                  onMouseLeave={() => setHoveredIcon(null)}
                  component={motion.a}
                  whileHover={{
                    scale: 1.2,
                    y: -3,
                    rotate: [-6, 6, 0],
                  }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                >
                  {social.icon}
                </MuiLink>
              ))}
            </Box>

            {/* Play Store Logo - Now Clickable */}
            <MuiLink
              href="https://play.google.com/store/apps/details?id=com.aediax.app"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-block',
                mt: 1.5,
                transition: 'all 0.3s ease',
                borderRadius: 2,
                '&:hover': {
                  transform: 'scale(1.08)',
                  filter: 'drop-shadow(0 0 20px rgba(0, 240, 255, 0.4))',
                },
              }}
              component={motion.a}
              whileHover={{ 
                scale: 1.08,
                rotate: [-2, 2, 0],
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15 }}
            >
              <Box
                component="img"
                src="https://raw.githubusercontent.com/AediaX/google-play-logo/refs/heads/main/Play%20store%20logo.png"
                alt="Download on Google Play"
                sx={{
                  width: 100,
                  height: 100,
                  objectFit: 'contain',
                  transition: 'all 0.3s ease',
                  filter: 'drop-shadow(0 0 10px rgba(0, 240, 255, 0.1))',
                  '&:hover': {
                    filter: 'drop-shadow(0 0 20px rgba(0, 240, 255, 0.2))',
                  },
                }}
              />
            </MuiLink>
          </Box>

          {/* Column 2: Quick Links */}
          <Box component={motion.div} variants={columnVariants(-10)}>
            <SectionHeading>Quick Links</SectionHeading>
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
              {mainLinks.slice(0, 8).map((link, index) => renderLink(link, index))}
            </Box>
          </Box>

          {/* Column 3: Services & Internships */}
          <Box component={motion.div} variants={columnVariants(0)}>
            <SectionHeading>Services</SectionHeading>
            <Box sx={{ mb: 1, mt: 1 }}>
              {servicesLinks.map((link, index) => renderLink(link, index))}
            </Box>
          </Box>

          {/* Column 4: Contact */}
          <Box component={motion.div} variants={columnVariants(10)}>
            <SectionHeading>Contact</SectionHeading>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 1,
                mt: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <LocationOn
                sx={{
                  mr: 1.5,
                  color: 'rgba(0, 240, 255, 0.4)',
                  fontSize: '0.9rem',
                  mt: 0.2,
                }}
              />
              <Typography variant="body2" sx={{ color: 'rgba(200, 200, 200, 0.6)', fontSize: '0.7rem', lineHeight: 1.4 }}>
                <strong style={{ color: 'rgba(200, 200, 200, 0.8)' }}>Bhubaneswar:</strong>
                <br />
                 Shampur, Ghatikia,
                <br />
                Bhubaneswar, Khorda – 751003
                <br />
                Odisha, India
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <LocationOn
                sx={{
                  mr: 1.5,
                  color: 'rgba(0, 240, 255, 0.4)',
                  fontSize: '0.9rem',
                  mt: 0.2,
                }}
              />
              <Typography variant="body2" sx={{ color: 'rgba(200, 200, 200, 0.6)', fontSize: '0.7rem', lineHeight: 1.4 }}>
                <strong style={{ color: 'rgba(200, 200, 200, 0.8)' }}>Baripada:</strong>
                <br />
                Baripada, Mayurbhanj - 757026
                <br />
                Odisha, India
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 0.8,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Phone
                sx={{
                  mr: 1.5,
                  color: 'rgba(0, 240, 255, 0.4)',
                  fontSize: '0.9rem',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(200, 200, 200, 0.6)',
                  fontSize: '0.7rem',
                  fontFamily: 'monospace',
                }}
              >
                +91 78549 98757
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1.5,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Email
                sx={{
                  mr: 1.5,
                  color: 'rgba(0, 240, 255, 0.4)',
                  fontSize: '0.9rem',
                }}
              />
              <MuiLink
                href="mailto:info@aediax.com"
                sx={{
                  color: 'rgba(200, 200, 200, 0.6)',
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#00f0ff',
                    textDecoration: 'underline',
                  },
                }}
                component={motion.a}
                whileHover={{ x: 3 }}
              >
                info@aediax.com
              </MuiLink>
            </Box>
          </Box>

          {/* Column 5: Platforms & Tech */}
          <Box component={motion.div} variants={columnVariants(20)}>
            <SectionHeading>Platforms</SectionHeading>
            <Box sx={{ mt: 1 }}>
              {subdomainLinks.map((link, index) => (
                <MuiLink
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 0.8,
                    color: 'rgba(200, 200, 200, 0.6)',
                    textDecoration: 'none',
                    fontSize: '0.7rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#00f0ff',
                      transform: 'translateX(4px)',
                    },
                  }}
                  component={motion.a}
                  whileHover={{ x: 4 }}
                >
                  <Box
                    component="span"
                    sx={{
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.2) rotate(-5deg)',
                      },
                    }}
                  >
                    {link.icon}
                  </Box>
                  {link.text}
                </MuiLink>
              ))}
            </Box>
            <SectionHeading small>Internships</SectionHeading>
            <Box sx={{ mt: 1 }}>
              {internshipLinks.map((link, index) => renderLink(link, index))}
            </Box>
          </Box>
        </Box>

        <PolicyLinks />

        <Divider
          sx={{
            my: 3,
            backgroundColor: 'rgba(0, 240, 255, 0.05)',
            borderBottomWidth: 1,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '20%',
              right: '20%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.2), transparent)',
            },
          }}
        />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            textAlign: { xs: 'center', sm: 'left' },
          }}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Box sx={{ order: { xs: 2, sm: 0 } }}>
            <Tooltip title="Developer Contact" arrow>
              <MuiLink
                component={motion.button}
                whileHover={{
                  scale: 1.05,
                  color: 'rgba(0, 240, 255, 0.6)',
                }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDialogOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'rgba(255, 255, 255, 0.37)',
                  textDecoration: 'none',
                  fontSize: '0.6rem',
                  bgcolor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'rgba(0, 240, 255, 0.4)',
                  },
                }}
              >
                <Code
                  sx={{
                    fontSize: '0.8rem',
                    mr: 0.5,
                    animation: `${floatAnimation} 3s ease-in-out infinite`,
                    ...reduceMotionSx,
                  }}
                />
                Developed by AediaX Team
              </MuiLink>
            </Tooltip>
          </Box>

          <Typography
            variant="body2"
            sx={{
             color: 'rgba(255, 255, 255, 0.37)',
              fontSize: '0.6rem',
              order: { xs: 1, sm: 0 },
              letterSpacing: '0.3px',
            }}
          >
            Transforming businesses with AI-driven innovation
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.37)',
              fontSize: '0.6rem',
              order: { xs: 3, sm: 0 },
              textAlign: { xs: 'center', sm: 'right' },
              letterSpacing: '0.3px',
            }}
          >
            © {new Date().getFullYear()}{' '}
            <Box component="span" sx={{ color: 'rgba(0, 240, 255, 0.6)' }}>
              Aedia
            </Box>
            <Box component="span" sx={{ color: 'rgba(255, 215, 0, 0.6)' }}>
              X
            </Box>
            . All Rights Reserved.
          </Typography>
        </Box>
      </Container>


      
        {/* Developer Contact Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#f0f2f5',
            borderRadius: 4,
            boxShadow: `
              -6px -6px 12px rgba(255, 255, 255, 0.8),
              6px 6px 12px rgba(0, 0, 0, 0.06),
              0 4px 20px rgba(0, 0, 0, 0.04)
            `,
            border: 'none',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2.5,
            px: 3,
            backgroundColor: '#f0f2f5',
            borderBottom: 'none',
          }}
        >
          <Box sx={{ fontSize: '1rem' }}>
            <Box 
              component="span" 
              sx={{ 
                color: '#1a1a2e',
                fontWeight: 700,
                letterSpacing: '-0.5px',
              }}
            >
              Aedia
              <Box 
                component="span" 
                sx={{ 
                  color: '#e0c23c',
                  fontWeight: 700,
                }}
              >
                X
              </Box>
            </Box>
            <Box 
              component="span" 
              sx={{ 
                fontSize: '0.65rem', 
                color: 'rgba(0, 0, 0, 0.3)',
                ml: 0.5,
                fontWeight: 400,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
              }}
            >
              Developer Contact
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            component={motion.button}
            whileHover={{ rotate: 90, scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            sx={{ 
              color: 'rgba(0, 0, 0, 0.25)',
              p: 0.5,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                color: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1, px: 3, pb: 1 }}>
          <DialogContentText
            sx={{
              color: 'rgba(0, 0, 0, 0.5)',
              mb: 2.5,
              fontSize: '0.8rem',
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            This website was developed by the AediaX development team. 
            We're passionate about creating innovative digital solutions.
          </DialogContentText>
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1.5,
              p: 2,
              borderRadius: 3,
              backgroundColor: '#f0f2f5',
              boxShadow: `
                -4px -4px 8px rgba(255, 255, 255, 0.7),
                4px 4px 8px rgba(0, 0, 0, 0.04)
              `,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: `
                  -6px -6px 12px rgba(255, 255, 255, 0.8),
                  6px 6px 12px rgba(0, 0, 0, 0.06)
                `,
                transform: 'translateY(-1px)',
              },
            }}
            component={motion.div}
            whileHover={{ x: 3 }}
          >
            <Phone
              sx={{
                mr: 1.5,
                color: '#1a1a2e',
                fontSize: '0.9rem',
                opacity: 0.4,
              }}
            />
            <MuiLink
              href="tel:+917854998757"
              sx={{
                color: '#1a1a2e',
                textDecoration: 'none',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                fontWeight: 500,
                letterSpacing: '0.3px',
                opacity: 0.7,
                '&:hover': { 
                  opacity: 1,
                },
              }}
            >
              +91 78549 98757
            </MuiLink>
          </Box>
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderRadius: 3,
              backgroundColor: '#f0f2f5',
              boxShadow: `
                -4px -4px 8px rgba(255, 255, 255, 0.7),
                4px 4px 8px rgba(0, 0, 0, 0.04)
              `,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: `
                  -6px -6px 12px rgba(255, 255, 255, 0.8),
                  6px 6px 12px rgba(0, 0, 0, 0.06)
                `,
                transform: 'translateY(-1px)',
              },
            }}
            component={motion.div}
            whileHover={{ x: 3 }}
          >
            <Email
              sx={{
                mr: 1.5,
                color: '#1a1a2e',
                fontSize: '0.9rem',
                opacity: 0.4,
              }}
            />
            <MuiLink
              href="mailto:development@aediax.com"
              sx={{
                color: '#1a1a2e',
                textDecoration: 'none',
                fontSize: '0.8rem',
                fontWeight: 500,
                opacity: 0.7,
                '&:hover': { 
                  opacity: 1,
                },
              }}
            >
              development@aediax.com
            </MuiLink>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          pt: 1.5,
          pb: 2.5,
          px: 3,
          backgroundColor: '#f0f2f5',
          borderTop: 'none',
        }}>
          <Button
            onClick={handleDialogClose}
            component={motion.button}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            sx={{
              color: '#1a1a2e',
              backgroundColor: '#f0f2f5',
              fontSize: '0.75rem',
              fontWeight: 500,
              py: 0.8,
              px: 3,
              borderRadius: 2,
              boxShadow: `
                -3px -3px 6px rgba(255, 255, 255, 0.7),
                3px 3px 6px rgba(0, 0, 0, 0.04)
              `,
              border: 'none',
              textTransform: 'none',
              letterSpacing: '0.3px',
              opacity: 0.6,
              '&:hover': {
                backgroundColor: '#f0f2f5',
                boxShadow: `
                  -4px -4px 8px rgba(255, 255, 255, 0.8),
                  4px 4px 8px rgba(0, 0, 0, 0.06)
                `,
                opacity: 1,
              },
            }}
            variant="contained"
            disableElevation
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Footer;